import styles from "./util.module.css"
import React from "react"
import * as fonts from "shared/fonts"
import cx from "classnames"
import * as money from "shared/money"
import * as finance from "shared/finance"

export const getYAxisRange = (dataMax) => (dataMax > 12000 ? Math.ceil(dataMax / 2000) * 2000 : Math.ceil(dataMax / 200) * 200)

// Function to provide an array of colors and to add theme support
export const colorsFor2Lines = (theme) => (theme ? ["#5C5761", `#${theme["color-1"]}`] : null)

export const getColor = (theme, num) => (theme ? `#${theme[`color-${num}`]}` : "#006AF9")

// Styles for recharts
export const xAxisTickStyles = {
  fontSize: "12",
  fontFamily: fonts.getFontFamily(),
  fill: "#5c5761",
  dy: 8
}

export const yAxisTickStyles = {
  fontSize: "12",
  fontFamily: fonts.getFontFamily(),
  fill: "#5c5761",
  dx: -4
}

// Function and Components for custom tooltips
const formatTooltipValue = (value, type) => (type === "currency" ? money.asShort(value) : type === "number" ? value : "")

const TooltipProperty = ({data, property, index, className = "node"}) => {
  const tooltip = data.fields[property.id].tooltip
  return (
    <div className={cx(styles[className], styles[tooltip.color])} style={{color: "#" + tooltip.color}}>
      <span>{tooltip.title}:</span>
      <span className={styles["value"]}>{formatTooltipValue(property.value, property.type)}</span>
    </div>
  )
}

export const CustomTooltip = ({payload, data}) => {
  if (payload && payload.length > 0) {
    const properties = Object.keys(data.fields)
      .filter((key) => typeof data.fields[key].tooltip === "object")
      .sort((a, b) => data.fields[a].tooltip.order - data.fields[b].tooltip.order)
      .map((key) => ({
        id: key,
        value: payload[0].payload[key],
        type: data.fields[key].type
      }))
    return (
      <div className="tooltip">
        {properties.map((p, i) => (
          <TooltipProperty key={p.id} data={data} property={p} index={i} />
        ))}
      </div>
    )
  } else {
    return null
  }
}

export const AxisLabel = ({className, label, type = "coach", customStyles}) => (
  <div className={cx(customStyles, styles[className], type && styles[`${type}-axis`], fonts.getFontClass())}>{label}</div>
)

export const Chart = ({className = "chart", type = "coach", children, ariaLabel}) => (
  <div className={cx(styles[className], type, fonts.getFontClass())} role="img" alt="Balance per year graph" aria-label={ariaLabel}>
    {children}
  </div>
)

/* Transform [[1, 2]] values to [{x: 1, y: 2}]  */
export const toRecharts = (values) =>
  values.map(([name, ...rest]) =>
    rest.reduce(
      (memo, vs, idx) => {
        memo[idx] = vs
        return memo
      },
      {name}
    )
  )

/*
 * e.g. If there are 13 values, then
 * it will fall within the [12, 3] breakpoint,
 * using groups of 3 for each tickmark.
 */
const _tickBreakdown = [
  [0, 1],
  [12, 3],
  [30, 5]
]

const xsmTickBreakdown = [
  [0, 1],
  [20, 6],
  [40, 10]
]

const tickBreakdownIndex = {
  small: xsmTickBreakdown,
  regular: _tickBreakdown
}

export const staggerTicks = (values, key = "regular") => {
  const tickBreakdown = tickBreakdownIndex[key]
  const count = values.length
  const tooFar = tickBreakdown.find(([breakpoint]) => count < breakpoint)
  const breakpoint = tickBreakdown[(tooFar ? tickBreakdown.indexOf(tooFar) : tickBreakdown.length) - 1]
  return values.map(([x]) => x).filter((v, idx) => idx % breakpoint[1] === 0)
}

const formatters = {
  default: (v) => v,
  currency: (v) => finance.asCurrency(v)
}

export const getFormatter = (formatter) => formatters[formatter]

/* Returns a child component for each set of Y axis values */
export const forEachSet = (values, f) => {
  if (values.length > 0) {
    return [...Array(values[0].length - 1)].map((x, idx) => {
      return f(idx)
    })
  } else {
    return null
  }
}
